<script setup lang="ts">
import { Deferred, Head, Link, router, useForm } from "@inertiajs/vue3"
import { FileText, SlidersHorizontal, X } from "lucide-vue-next"
import { computed, ref, watch } from "vue"

import AddressForm, { type AddressFields } from "@/components/AddressForm.vue"
import AppDataTable from "@/components/AppDataTable.vue"
import type { TableColumn } from "@/components/AppDataTable.vue"
import AppPagination from "@/components/AppPagination.vue"
import FormSection from "@/components/FormSection.vue"
import HeadingSmall from "@/components/HeadingSmall.vue"
import InputError from "@/components/InputError.vue"
import RelativeTime from "@/components/RelativeTime.vue"
import StatusBadge from "@/components/applications/StatusBadge.vue"
import { type Comment } from "@/components/comments/CommentItem.vue"
import CommentThread from "@/components/comments/CommentThread.vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import AppLayout from "@/layouts/AppLayout.vue"
import LenderLayout from "@/layouts/lenders/Layout.vue"
import { formatAmount, formatLabel } from "@/lib/utils"
import {
  editLenderPath,
  editLoanApplicationPath,
  lenderAddressPath,
  lenderCommentsPath,
  lenderPath,
  lendersPath,
} from "@/routes"
import { type BreadcrumbItem } from "@/types"

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Lender {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  portal: string | null
  has_api: boolean
  active: boolean
  adapter: string | null
}

interface Address {
  id?: number
  unit_no: string | null
  street_no: string
  street_name: string
  street_type: string
  city: string
  state: string
  postcode: string
}

interface Application {
  id: string
  status: string
  status_at: string | null
  rejection_reason: string | null
  loan_type: string
  purpose: string
  amount: number
  settled_at: string | null
  created_at: string
}

interface AppFilters {
  loan_type?: string | null
  purpose?: string | null
  direction?: string | null
}

const props = defineProps<{
  lender: Lender
  canManageApiSettings?: boolean
  address?: Address | null
  applications?: { records: Application[]; pagination: PaginationMeta; filters: AppFilters } | null
  comments?: Comment[] | null
  loan_types: string[]
  purposes: Record<string, string[]>
}>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Lenders", href: lendersPath() },
  { title: props.lender.name, href: editLenderPath(props.lender.id) },
]

const urlSection = new URLSearchParams(window.location.search).get("section")
const validSections = ["details", "address", ...(props.canManageApiSettings ? ["api_settings"] : []), "applications"] as const
type Section = "details" | "address" | "api_settings" | "applications"
const activeSection = ref<Section>(
  (validSections as readonly string[]).includes(urlSection ?? "") ? (urlSection as Section) : "details",
)

const detailsForm = useForm({
  name: props.lender.name,
  contact: props.lender.contact,
  email: props.lender.email,
  phone: props.lender.phone,
  portal: props.lender.portal ?? "",
  has_api: props.lender.has_api,
  active: props.lender.active,
})

function submitDetails() {
  detailsForm.patch(lenderPath(props.lender.id), {
    preserveScroll: true,
  })
}

const ADAPTER_OPTIONS = [
  { value: "money3", label: "Money3" },
  { value: "money_me", label: "MoneyMe" },
  { value: "moneyplace", label: "Moneyplace" },
  { value: "plenti", label: "Plenti" },
  { value: "rapid_loans", label: "Rapid Loans" },
  { value: "society_one", label: "Society One" },
  { value: "wisr", label: "Wisr" },
] as const

const NONE_VALUE = "__none__"

const apiSettingsForm = useForm({
  adapter: props.lender.adapter ?? "",
})

const selectedAdapter = computed({
  get: () => apiSettingsForm.adapter || NONE_VALUE,
  set: (val: string) => {
    apiSettingsForm.adapter = val === NONE_VALUE ? "" : val
  },
})

function submitApiSettings() {
  apiSettingsForm.patch(lenderPath(props.lender.id), {
    preserveScroll: true,
  })
}

const addressForm = useForm({
  unit_no: "",
  street_no: "",
  street_name: "",
  street_type: "",
  city: "",
  state: "",
  postcode: "",
})

watch(
  () => props.address,
  (addr) => {
    if (!addr) return
    Object.assign(addressForm, {
      unit_no: addr.unit_no ?? "",
      street_no: addr.street_no,
      street_name: addr.street_name,
      street_type: addr.street_type,
      city: addr.city,
      state: addr.state,
      postcode: addr.postcode,
    })
  },
  { immediate: true },
)

const addressFields = computed<AddressFields>({
  get: () => ({
    unit_no: addressForm.unit_no,
    street_no: addressForm.street_no,
    street_name: addressForm.street_name,
    street_type: addressForm.street_type,
    city: addressForm.city,
    state: addressForm.state,
    postcode: addressForm.postcode,
  }),
  set: (val) => Object.assign(addressForm, val),
})

function submitAddress() {
  addressForm.patch(lenderAddressPath(props.lender.id), { preserveScroll: true })
}

// ── Applications filters & sort ───────────────────────────────────────────
const ALL = "__all__"
const urlParams = new URLSearchParams(window.location.search)

const appLoanTypeFilter = ref(urlParams.get("loan_type") ?? ALL)
const appPurposeFilter = ref(urlParams.get("purpose") ?? ALL)
const appSortDir = ref<"asc" | "desc">((urlParams.get("direction") as "asc" | "desc") ?? "desc")

const availablePurposes = computed(() =>
  appLoanTypeFilter.value !== ALL
    ? (props.purposes[appLoanTypeFilter.value] ?? [])
    : Object.values(props.purposes).flat(),
)

watch(appLoanTypeFilter, (newType) => {
  if (appPurposeFilter.value === ALL) return
  const valid = newType !== ALL ? (props.purposes[newType] ?? []) : Object.values(props.purposes).flat()
  if (!valid.includes(appPurposeFilter.value)) appPurposeFilter.value = ALL
})

const hasAppFilters = computed(
  () => appLoanTypeFilter.value !== ALL || appPurposeFilter.value !== ALL,
)

const appFilterParams = computed(() => ({
  section: "applications",
  loan_type: appLoanTypeFilter.value !== ALL ? appLoanTypeFilter.value : undefined,
  purpose: appPurposeFilter.value !== ALL ? appPurposeFilter.value : undefined,
  direction: appSortDir.value !== "desc" ? appSortDir.value : undefined,
}))

function applyAppFilters() {
  const params = Object.fromEntries(
    Object.entries(appFilterParams.value).filter(([, v]) => v != null),
  )
  router.get(editLenderPath(props.lender.id), params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

watch([appLoanTypeFilter, appPurposeFilter], applyAppFilters)

function clearAppFilters() {
  appLoanTypeFilter.value = ALL
  appPurposeFilter.value = ALL
}

function handleAppSort(_key: string, dir: "asc" | "desc") {
  appSortDir.value = dir
  applyAppFilters()
}

const appTableColumns: TableColumn[] = [
  { key: "purpose", label: "Purpose" },
  { key: "amount", label: "Amount" },
  { key: "status", label: "Status" },
  { key: "settled_at", label: "Settled", sortable: true },
  { key: "actions", label: "", align: "right" },
]
</script>

<template>
  <Head :title="`Edit ${lender.name}`" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <LenderLayout
      v-model:active-section="activeSection"
      :lender-id="lender.id"
      :lender-name="lender.name"
      :can-manage-api-settings="canManageApiSettings"
    >
      <!-- ── Details section ───────────────────────────────────────────── -->
      <div v-show="activeSection === 'details'" class="space-y-6">
        <HeadingSmall
          title="Lender details"
          description="Update the lender's name and contact information"
        />

        <form class="space-y-4" @submit.prevent="submitDetails">
          <FormSection title="Contact Information">
            <div class="grid gap-2">
              <Label for="name">Name</Label>
              <Input id="name" v-model="detailsForm.name" placeholder="ANZ Bank" required />
              <InputError :messages="detailsForm.errors.name" />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="grid gap-2">
                <Label for="contact">Contact Person</Label>
                <Input id="contact" v-model="detailsForm.contact" placeholder="John Smith" required />
                <InputError :messages="detailsForm.errors.contact" />
              </div>
              <div class="grid gap-2">
                <Label for="phone">Phone</Label>
                <Input id="phone" v-model="detailsForm.phone" type="tel" placeholder="+61 400 000 000" required />
                <InputError :messages="detailsForm.errors.phone" />
              </div>
            </div>

            <div class="grid gap-2">
              <Label for="email">Email</Label>
              <Input id="email" v-model="detailsForm.email" type="email" placeholder="contact@lender.com" required />
              <InputError :messages="detailsForm.errors.email" />
            </div>

            <div class="grid gap-2">
              <Label for="portal">Portal URL <span class="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="portal" v-model="detailsForm.portal" type="url" placeholder="https://portal.lender.com" />
              <InputError :messages="detailsForm.errors.portal" />
            </div>
          </FormSection>

          <div class="border-border flex items-center justify-between rounded-lg border p-4">
            <div>
              <p class="text-sm font-medium">Has API</p>
              <p class="text-muted-foreground text-xs">Enables API submission of applications to this lender.</p>
            </div>
            <Switch id="has-api" v-model="detailsForm.has_api" />
          </div>

          <div class="border-border flex items-center justify-between rounded-lg border p-4">
            <div>
              <p class="text-sm font-medium">Active</p>
              <p class="text-muted-foreground text-xs">Allow this lender to receive and process loan applications.</p>
            </div>
            <Switch id="active" v-model="detailsForm.active" />
          </div>

          <div class="flex items-center gap-4 pt-2">
            <Button type="submit" :disabled="detailsForm.processing">Save Changes</Button>
            <Transition
              enter-active-class="transition ease-in-out"
              enter-from-class="opacity-0"
              leave-active-class="transition ease-in-out"
              leave-to-class="opacity-0"
            >
              <p v-show="detailsForm.recentlySuccessful" class="text-muted-foreground text-sm">
                Saved.
              </p>
            </Transition>
          </div>
        </form>
      </div>

      <!-- ── Address section ───────────────────────────────────────────── -->
      <div v-show="activeSection === 'address'" class="space-y-6">
        <HeadingSmall
          title="Address"
          description="Update the lender's physical address"
        />

        <Deferred data="address">
          <template #fallback>
            <div class="space-y-4">
              <div class="grid grid-cols-3 gap-4">
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
              </div>
              <Skeleton class="h-9" />
              <div class="grid grid-cols-3 gap-4">
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
              </div>
            </div>
          </template>

          <form class="space-y-4" @submit.prevent="submitAddress">
            <FormSection title="Address">
              <AddressForm v-model="addressFields" :errors="addressForm.errors" />
            </FormSection>

            <div class="flex items-center gap-4 pt-2">
              <Button type="submit" :disabled="addressForm.processing">Save Address</Button>
              <Transition
                enter-active-class="transition ease-in-out"
                enter-from-class="opacity-0"
                leave-active-class="transition ease-in-out"
                leave-to-class="opacity-0"
              >
                <p v-show="addressForm.recentlySuccessful" class="text-muted-foreground text-sm">
                  Saved.
                </p>
              </Transition>
            </div>
          </form>
        </Deferred>
      </div>

      <!-- ── API Settings section ──────────────────────────────────────── -->
      <div v-if="canManageApiSettings" v-show="activeSection === 'api_settings'" class="space-y-6">
        <HeadingSmall
          title="API Settings"
          description="Configure the API integration for this lender"
        />

        <form class="space-y-4" @submit.prevent="submitApiSettings">
          <FormSection title="Adapter">
            <div class="grid gap-2">
              <Label for="adapter">Adapter</Label>
              <Select v-model="selectedAdapter" :disabled="!detailsForm.has_api">
                <SelectTrigger id="adapter" class="w-full sm:w-64">
                  <SelectValue placeholder="No adapter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem :value="NONE_VALUE">None</SelectItem>
                  <SelectItem v-for="opt in ADAPTER_OPTIONS" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p v-if="!detailsForm.has_api" class="text-muted-foreground text-xs">
                Enable "Has API" in Details to configure an adapter.
              </p>
              <InputError :messages="apiSettingsForm.errors.adapter" />
            </div>
          </FormSection>

          <div class="flex items-center gap-4 pt-2">
            <Button type="submit" :disabled="apiSettingsForm.processing || !detailsForm.has_api">Save</Button>
            <Transition
              enter-active-class="transition ease-in-out"
              enter-from-class="opacity-0"
              leave-active-class="transition ease-in-out"
              leave-to-class="opacity-0"
            >
              <p v-show="apiSettingsForm.recentlySuccessful" class="text-muted-foreground text-sm">
                Saved.
              </p>
            </Transition>
          </div>
        </form>
      </div>

      <!-- ── Applications section ──────────────────────────────────────── -->
      <div v-show="activeSection === 'applications'" class="space-y-4">
        <HeadingSmall
          title="Applications"
          description="All loan applications assigned to this lender"
        />

        <!-- Filters -->
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select v-model="appLoanTypeFilter">
            <SelectTrigger class="w-full sm:w-40">
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any type</SelectItem>
              <SelectItem v-for="t in props.loan_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
            </SelectContent>
          </Select>
          <Select v-model="appPurposeFilter">
            <SelectTrigger class="w-full sm:w-44">
              <SelectValue placeholder="Any purpose" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any purpose</SelectItem>
              <SelectItem v-for="p in availablePurposes" :key="p" :value="p">{{ formatLabel(p) }}</SelectItem>
            </SelectContent>
          </Select>
          <Button v-if="hasAppFilters" variant="ghost" size="sm" class="text-muted-foreground hover:text-foreground shrink-0" @click="clearAppFilters">
            <X class="size-4" />
            Clear
          </Button>
        </div>

        <Deferred data="applications">
          <template #fallback>
            <div class="space-y-2">
              <Skeleton class="h-10 w-full" />
              <Skeleton class="h-10 w-full" />
              <Skeleton class="h-10 w-full" />
            </div>
          </template>

          <div v-if="!applications || applications.records.length === 0" class="flex flex-col items-center gap-3 py-10 text-center">
            <FileText class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasAppFilters ? "No applications match your filters" : "No applications yet" }}
            </p>
            <Button v-if="hasAppFilters" variant="outline" size="sm" @click="clearAppFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <div v-else class="overflow-x-auto rounded-lg border">
            <div class="border-border flex items-center justify-between border-b px-4 py-2">
              <span class="text-muted-foreground text-xs">
                {{ applications.pagination.total_count }} application{{ applications.pagination.total_count !== 1 ? "s" : "" }}
                {{ hasAppFilters ? "found" : "total" }}
              </span>
              <SlidersHorizontal class="text-muted-foreground size-3.5" />
            </div>
            <AppDataTable
              :columns="appTableColumns"
              sort="settled_at"
              :direction="appSortDir"
              compact
              @sort="handleAppSort"
            >
              <tr
                v-for="app in applications.records"
                :key="app.id"
                class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
              >
                <td class="px-4 py-3 font-medium">{{ formatLabel(app.purpose) }}</td>
                <td class="text-muted-foreground px-4 py-3">{{ formatAmount(app.amount) }}</td>
                <td class="px-4 py-3"><StatusBadge :status="app.status" :status-at="app.status_at" :rejection-reason="app.rejection_reason" /></td>
                <td class="text-muted-foreground px-4 py-3"><RelativeTime :date="app.settled_at ?? ''" /></td>
                <td class="px-4 py-3 text-right">
                  <Link :href="editLoanApplicationPath(app.id)" class="text-primary text-xs hover:underline">Open →</Link>
                </td>
              </tr>
            </AppDataTable>
            <AppPagination
              :current-page="applications.pagination.current_page"
              :total-pages="applications.pagination.total_pages"
              :total-count="applications.pagination.total_count"
              :per-page="applications.pagination.per_page"
              :base-url="editLenderPath(lender.id)"
              :extra-params="appFilterParams"
            />
          </div>
        </Deferred>
      </div>
    </LenderLayout>

    <!-- Activity & Comments — shown under details section -->
    <div v-if="activeSection === 'details'" class="flex max-w-7xl flex-col px-4 pb-6 lg:flex-row lg:space-x-12">
      <div class="hidden lg:block lg:w-48 lg:shrink-0" />
      <div class="flex-1">
        <Deferred data="comments">
          <template #fallback>
            <div class="mt-8 space-y-2">
              <div class="bg-muted h-4 w-48 animate-pulse rounded" />
              <div class="bg-muted h-16 animate-pulse rounded" />
              <div class="bg-muted h-16 animate-pulse rounded" />
            </div>
          </template>
          <CommentThread
            :comments="comments ?? []"
            :create-path="lenderCommentsPath(lender.id)"
            :can-comment="true"
          />
        </Deferred>
      </div>
    </div>
  </AppLayout>
</template>
